import api from "./api";
import Strings from "../constants/Strings";

function getCookie(name: string) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(
          trimmedCookie.substring(name.length + 1)
        );
        break;
      }
    }
  }
  return cookieValue;
}

interface AssistantChatRequest {
  assistant_id: number;
  message: string;
  conversation_history?: Array<{ role: "user" | "assistant"; content: string }>;
  audio?: Blob;
  image?: File;
  user_id?: number | string;
}

interface AssistantChatResponse {
  success: boolean;
  response: string;
  assistant_name?: string;
  used_tools?: boolean;
  metadata?: {
    temperature?: number;
    exercises_enabled?: boolean;
  };
  error?: string;
}

interface ProgressData {
  step: string;
  message: string;
  exercises_count: number;
}

interface StreamEvent {
  type: "progress" | "result" | "error";
  data:
    | ProgressData
    | AssistantChatResponse
    | { success: boolean; error?: string };
}

interface AssistantListResponse {
  id: number;
  name: string;
  description: string;
  temperatura: number;
  exercicies_use: boolean;
  tools_count: number;
  timestamp: string;
}

const STREAM_TIMEOUT_MS = 5 * 60 * 1000;

async function chat(
  request: AssistantChatRequest
): Promise<AssistantChatResponse> {
  try {
    const { data } = await api.post<AssistantChatResponse>(
      "/assistants/chat/",
      request
    );
    return data;
  } catch (e: unknown) {
    console.error("Erro ao chamar assistant:", e);
    const error = e as { response?: { data?: { error?: string } } };
    return {
      success: false,
      response: "",
      error: error.response?.data?.error || "Erro ao processar mensagem",
    };
  }
}

async function chatStream(
  request: AssistantChatRequest,
  onProgress?: (progress: ProgressData) => void
): Promise<AssistantChatResponse> {
  let didTimeout = false;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, STREAM_TIMEOUT_MS);

  try {
    // Usa a mesma lógica do interceptor do axios para obter token e CSRF
    const token = localStorage.getItem(Strings.token_jwt);
    const csrfToken = getCookie("csrftoken");
    
    // Se houver arquivos (áudio ou imagem), usa FormData, senão usa JSON
    const hasFiles = request.audio || request.image;
    
    let body: FormData | string;
    const headers: HeadersInit = {};

    if (hasFiles) {
      // Usa FormData para enviar arquivos
      const formData = new FormData();
      formData.append("assistant_id", request.assistant_id.toString());
      formData.append("message", request.message || "");
      
      if (request.conversation_history) {
        formData.append("conversation_history", JSON.stringify(request.conversation_history));
      }
      
      if (request.user_id) {
        formData.append("user_id", request.user_id.toString());
      }
      
      if (request.audio) {
        formData.append("audio", request.audio, "recording.webm");
      }
      
      if (request.image) {
        formData.append("image", request.image);
      }
      
      body = formData;
      // Não define Content-Type para FormData, o browser define automaticamente com boundary
    } else {
      // Usa JSON quando não há arquivos
      headers["Content-Type"] = "application/json";
      body = JSON.stringify({
        assistant_id: request.assistant_id,
        message: request.message,
        conversation_history: request.conversation_history,
        user_id: request.user_id,
      });
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (csrfToken) {
      headers["X-CSRFToken"] = csrfToken;
    }

    const response = await fetch(
      `${api.defaults.baseURL}/assistants/chat/stream/`,
      {
        method: "POST",
        headers,
        body,
        credentials: api.defaults.withCredentials ? "include" : "same-origin",
        signal: controller.signal,
      }
    );

    if (!response.ok) {
      if (response.status === 504 || response.status === 408) {
        throw new Error(
          "Timeout da requisição de IA após 5 minutos. Tente novamente."
        );
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let finalResult: AssistantChatResponse | null = null;

    if (!reader) {
      throw new Error("Response body não disponível para streaming");
    }

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          try {
            const eventData: StreamEvent = JSON.parse(line.slice(6));

            if (eventData.type === "progress" && onProgress) {
              onProgress(eventData.data as ProgressData);
            } else if (eventData.type === "result") {
              finalResult = eventData.data as AssistantChatResponse;
            } else if (eventData.type === "error") {
              const errorData = eventData.data as {
                success: boolean;
                error?: string;
              };
              return {
                success: false,
                response: "",
                error: errorData.error || "Erro ao processar mensagem",
              };
            }
          } catch (e) {
            console.error("Erro ao parsear evento SSE:", e);
          }
        }
      }
    }

    if (finalResult) {
      return finalResult;
    }

    return {
      success: false,
      response: "",
      error: "Resposta incompleta do servidor",
    };
  } catch (e: unknown) {
    console.error("Erro ao chamar assistant stream:", e);
    const error = e as { message?: string; name?: string };
    const isAbortError = error.name === "AbortError";

    if (didTimeout || isAbortError) {
      return {
        success: false,
        response: "",
        error: "Timeout da requisição de IA após 5 minutos. Tente novamente.",
      };
    }

    return {
      success: false,
      response: "",
      error: error.message || "Erro ao processar mensagem",
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

async function listAssistants(): Promise<AssistantListResponse[]> {
  try {
    const { data } = await api.get<AssistantListResponse[]>(
      "/assistants/list/"
    );
    return data;
  } catch (e) {
    console.error("Erro ao listar assistentes:", e);
    return [];
  }
}

async function notRecommendExercise(
  exerciseId: number,
  userId?: number | string | null,
  forAll: boolean = false
): Promise<{ success: boolean; message?: string; error?: string; for_all?: boolean }> {
  try {
    const payload: {
      exercise_id: number;
      user_id?: number | string | null;
      for_all?: boolean;
    } = {
      exercise_id: exerciseId,
    };

    if (forAll) {
      payload.for_all = true;
    } else if (userId) {
      payload.user_id = userId;
    }

    const { data } = await api.post("/assistants/not-recommend-exercise/", payload);
    return { 
      success: true, 
      message: data.message,
      for_all: data.for_all,
    };
  } catch (e: unknown) {
    console.error("Erro ao marcar exercício como não recomendado:", e);
    const error = e as { response?: { data?: { error?: string } } };
    return {
      success: false,
      error: error.response?.data?.error || "Erro ao processar solicitação",
    };
  }
}

const assistant = {
  chat,
  chatStream,
  listAssistants,
  notRecommendExercise,
};

export default assistant;
export type { ProgressData };
